import { Hyperlink } from "@Light/components/page/Hyperlink";
import TexasIcon from "@Light/icons/TexasIcon";
import type { Requirements } from "@Light/services/lightTypes";
import { UtilityIdentifier } from "@Light/services/lightTypes";
import { Home02 } from "@untitledui/icons";
import type { SVGProps } from "react";
import { formatPlanName } from "./formatting";

export type TrustDisplay = {
  icon: React.FC<SVGProps<SVGSVGElement>>;
  text: string;
  license?: string;
};

export type UtilityBehavior = {
  /** How a utility's address and account number should be looked up. */
  serviceLocationEntry: "search" | "manual";
  /** How the utility refers to utility account numbers. */
  accountNumberName: string;
  /** Description of how to find the utility account number. */
  accountNumberDescription?: React.ReactNode;
  /** Statement disclosing our supplier license with the state regulator. */
  disclosureTerms: React.ReactNode;
  /** Statement authorizing us to start service on the customer's behalf. */
  makeAuthorizationTerms: ((planName: string) => React.ReactNode) | null;
  /** Trust badge shown under the "Powered by Light" attribution. */
  trustDisplay: TrustDisplay;
};

/** Fallback behavior for unknown utility. */
export const DEFAULT_UTILITY_BEHAVIOR: UtilityBehavior = {
  accountNumberName: "Account number",
  // Fallback to search for unknown utilities. Their search will only match up
  // to known addresses from known utilities.
  serviceLocationEntry: "search",
  // Components are responsible for handling falsy values when we don't have terms to show.
  disclosureTerms: null,
  makeAuthorizationTerms: null,
  trustDisplay: { icon: Home02, text: "Trusted by thousands of neighbors" },
};

const ERCOT: UtilityBehavior = {
  ...DEFAULT_UTILITY_BEHAVIOR,
  accountNumberName: "ESI ID",
  serviceLocationEntry: "search",
  disclosureTerms: `I have read, understand and accept the disclosures and
  terms of service of Light, PUCT #10340.`,
  makeAuthorizationTerms: (planName) =>
    `By selecting ‘Complete enrollment,’ I designate Light to perform necessary
     tasks to initiate my service with the ${formatPlanName(planName)}.`,
  trustDisplay: {
    icon: TexasIcon,
    text: "Trusted by thousands of Texans",
    license: "PUCT #10340",
  },
};

const PJM: UtilityBehavior = {
  ...DEFAULT_UTILITY_BEHAVIOR,
  serviceLocationEntry: "manual",
  // TODO: Needs legal sign-off
  disclosureTerms: `I have read, understand and accept the Disclosure Statement
  and Contract Summary of Light Energy, LLC d/b/a Light Energy Group, PA PUC
  license #A-2026-3062117.`,
  // TODO: Needs legal sign-off
  makeAuthorizationTerms: (planName) =>
    `By selecting ‘Complete enrollment,’ I authorize Light to change my electric
    power supplier and to perform necessary tasks to initiate my service with
    the ${formatPlanName(planName)}.`,
  trustDisplay: {
    ...DEFAULT_UTILITY_BEHAVIOR.trustDisplay,
    license: "PA PUC #A-2026-3062117",
  },
};

const UTILITY_BEHAVIOR: Record<UtilityIdentifier, UtilityBehavior> = {
  [UtilityIdentifier.AepCentral]: ERCOT,
  [UtilityIdentifier.AepNorth]: ERCOT,
  [UtilityIdentifier.Centerpoint]: ERCOT,
  [UtilityIdentifier.Lubbock]: ERCOT,
  [UtilityIdentifier.Oncor]: ERCOT,
  [UtilityIdentifier.Tnmp]: ERCOT,
  [UtilityIdentifier.Peco]: {
    ...PJM,
    accountNumberName: "Electric Choice ID",
    accountNumberDescription: (
      <>
        Your Electric Choice ID is a 10-digit number that can be located in the
        Shopping Information Box on the second page of your PECO bill, or can be
        found by{" "}
        <Hyperlink
          href="https://secure.peco.com/Pages/Login.aspx"
          target="_blank"
        >
          logging into your PECO account
        </Hyperlink>
        .
      </>
    ),
  },
  [UtilityIdentifier.Ppl]: {
    ...PJM,
    accountNumberDescription: (
      <>
        Your account number is a 10-digit number that can be found at the
        top-right of your bill, or can be found by{" "}
        <Hyperlink
          href="https://selfserve.pplelectric.com/sign-in"
          target="_blank"
        >
          logging into your PPL account
        </Hyperlink>
        .
      </>
    ),
  },
};

export function getUtilityBehavior(
  utility: UtilityIdentifier | undefined,
): UtilityBehavior {
  return (utility && UTILITY_BEHAVIOR[utility]) || DEFAULT_UTILITY_BEHAVIOR;
}

export function getUtilityBehaviorFromRequirements(
  requirements: Requirements,
): UtilityBehavior {
  return getUtilityBehavior(requirements.utility?.value?.utility?.id);
}
